/**
 * "Insight of the Day", a single, beautiful, evergreen insight shown once on the first open of
 * each day. Deliberately NOT news: a thoughtful mental model, a useful fact, or a sharp idea that
 * rewards the reader for showing up. Picked deterministically by the date (stable for the whole
 * day, rotates daily) and gently personalised to the reader's top category when we know it.
 *
 * Content is curated (not generated) so it stays on-brand: "Pure Signal, No Noise."
 */

export type DailyInsight = {
  id: string;
  /** A short category-ish eyebrow, e.g. "Mental model", "On focus". */
  eyebrow: string;
  /** The insight itself, punchy, quotable, one or two sentences. */
  text: string;
  /** One sentence of useful elaboration / why it matters. */
  detail: string;
  /** Maps to an anchor category for light personalisation (or "General"). */
  category: string;
};

export const DAILY_INSIGHTS: DailyInsight[] = [
  {
    id: "map-territory",
    eyebrow: "Mental model",
    text: "The map is not the territory.",
    detail:
      "Every model is a simplification: trust it where it predicts, and trust reality over it the moment they disagree.",
    category: "Science & Systems",
  },
  {
    id: "compounding",
    eyebrow: "On time",
    text: "Small advantages, repeated daily, beat big bursts.",
    detail:
      "1% better each day compounds to ~37× in a year; consistency out-earns intensity almost everywhere it's measured.",
    category: "Money & Work",
  },
  {
    id: "second-order",
    eyebrow: "On decisions",
    text: "Ask: and then what?",
    detail:
      "Most mistakes are first-order wins with worse second-order costs; the best decisions look one move further ahead.",
    category: "Power & Policy",
  },
  {
    id: "attention",
    eyebrow: "On focus",
    text: "Your attention is the most valuable thing you own.",
    detail:
      "It is also the thing most companies are paid to take: spend it on purpose, the way you'd spend money.",
    category: "Technology & Tools",
  },
  {
    id: "feynman",
    eyebrow: "On understanding",
    text: "If you can't explain it simply, you don't understand it yet.",
    detail:
      "Teaching an idea in plain words is the fastest test of whether you actually grasp it: Feynman's whole method.",
    category: "Science & Systems",
  },
  {
    id: "trees",
    eyebrow: "Worth knowing",
    text: "There are more trees on Earth than stars in the Milky Way.",
    detail:
      "~3 trillion trees vs ~100-400 billion stars, a reminder that the everyday world is often grander than the cosmic one.",
    category: "Planet & Environment",
  },
  {
    id: "sleep",
    eyebrow: "On the body",
    text: "Sleep is the closest thing to a free performance enhancer.",
    detail:
      "It consolidates memory, repairs the body, and clears metabolic waste from the brain; nothing else does all three.",
    category: "Science & Systems",
  },
  {
    id: "inversion",
    eyebrow: "Mental model",
    text: "To find the right path, study how to fail, then avoid it.",
    detail:
      "Inversion: solving 'what would guarantee disaster?' is often easier and clearer than 'what guarantees success?'.",
    category: "Money & Work",
  },
  {
    id: "octopus",
    eyebrow: "Worth knowing",
    text: "An octopus has three hearts and thinks partly with its arms.",
    detail:
      "Two-thirds of its neurons live in its limbs; intelligence doesn't have to be centralised the way ours is.",
    category: "Culture & Curiosity",
  },
  {
    id: "marginal",
    eyebrow: "On progress",
    text: "Champions are built from boring fundamentals done well.",
    detail:
      "Marginal gains: a team that improves dozens of tiny, unglamorous details usually beats one chasing a single silver bullet.",
    category: "Sport & Play",
  },
  {
    id: "default",
    eyebrow: "On design",
    text: "The default is the decision.",
    detail:
      "Most people never change a setting, so whoever sets the default quietly makes the choice for millions.",
    category: "Technology & Tools",
  },
  {
    id: "honey",
    eyebrow: "Worth knowing",
    text: "Honey never spoils.",
    detail:
      "Archaeologists have eaten 3,000-year-old honey from Egyptian tombs; its chemistry makes it almost immortal.",
    category: "Culture & Curiosity",
  },
  {
    id: "stoic",
    eyebrow: "Thoughtful words",
    text: "You have power over your mind, not outside events. Realise this, and you'll find strength.",
    detail: "Marcus Aurelius, the core of Stoic practice: spend energy on what you can actually move.",
    category: "Culture & Curiosity",
  },
  {
    id: "energy-light",
    eyebrow: "On the planet",
    text: "Enough sunlight hits the Earth in one hour to power the world for a year.",
    detail:
      "The hard part was never the energy; it's the cost of capturing and storing it, which keeps falling fast.",
    category: "Planet & Environment",
  },
  {
    id: "pareto",
    eyebrow: "Mental model",
    text: "Roughly 80% of results come from 20% of effort.",
    detail:
      "Find the vital few inputs that drive most of the output, then protect them, and feel free to neglect the rest.",
    category: "Money & Work",
  },
  {
    id: "curiosity",
    eyebrow: "Thoughtful words",
    text: "The cure for boredom is curiosity. There is no cure for curiosity.",
    detail: "Dorothy Parker, a good reason to read one genuinely new thing every day.",
    category: "Culture & Curiosity",
  },
];

/** A tiny, stable string hash → non-negative int (so the pick is deterministic per day). */
function hashKey(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) {
    h = (Math.imul(31, h) + key.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * The insight is a PRIZE, not a doorman: it only ever shows to a reader who has finished at
 * least one round (the completion handler stamps this key). A brand-new visitor goes straight
 * to the cards and meets the insight after their first finished round instead of before it.
 */
export const ROUND_DONE_KEY = "insnip_round_done_once";

/** Whether this reader has ever finished a round, read through an injectable storage getter
 *  (localStorage in the app, a plain map in tests). Storage failures read as "not yet". */
export function hasFinishedARound(get: (key: string) => string | null): boolean {
  try {
    return get(ROUND_DONE_KEY) != null;
  } catch {
    return false;
  }
}

/**
 * Pick the day's insight: stable for the whole of `dateKey`, gently biased toward the reader's
 * `topCategory` when some insights match it (else the full set rotates by date).
 */
export function pickInsightOfDay(dateKey: string, topCategory?: string | null): DailyInsight {
  const pool =
    topCategory && DAILY_INSIGHTS.some((i) => i.category === topCategory)
      ? DAILY_INSIGHTS.filter((i) => i.category === topCategory)
      : DAILY_INSIGHTS;
  return pool[hashKey(dateKey) % pool.length];
}
